import { useMatches } from 'react-router-dom';
import { Button, Box, Breadcrumbs } from '@mui/material';
import permissionConfig from '../../../config/permissionConfig';
import { useUserAccess } from '../../providers/UserAccessProvider';
import { useLocalization } from '../../providers/LocalizationProvider';

/*

e.g 
{
    "path": "create",
    "element": "CompanyInfoCreate",
    "crumb": {
        "id": "CREATE_SERVICE_PROVIDER",
        "defaultMessage": "Create Service Provider"
    },
    "icon": "ServiceProvider",
    "permissionName": "CAN_LIST_SP"
}
*/

function interpolateUrl(routeString, params) {
  if (typeof routeString !== 'string') return routeString;
  return routeString.replace(/{([^}]+)}/g, (_, key) => params?.[key] ?? '');
}

function resolveCrumbLabel(crumb, match, data, formatMessage) {
  const value = typeof crumb === 'function' ? crumb(match) : crumb;

  if (value && typeof value === 'object') {
    return formatMessage(
      { id: value.id, defaultMessage: value.defaultMessage },
      data,
    );
  }
  return interpolateUrl(value, data);
}

function truncateString(str, maxLength = 20) {
  if (typeof str !== 'string') return str;
  if (str.length <= maxLength) return str;
  return `${str.slice(0, 8)}...${str.slice(-6)}`;
}

function BreadcrumbRouterBased({
  data,
  truncateLabelAfter = 20,
  IconLibrary = [],
  ...breadcrumbProps
}) {
  const matches = useMatches();
  const userAccess = useUserAccess();
  const { formatMessage } = useLocalization();

  const breadcrumbs = matches
    .filter(match => match.handle?.crumb)
    .map(match => {
      const { crumb, icon, permissionName = null } = match.handle;
      const IconComponent = icon ? IconLibrary?.[icon] || null : null;

      return {
        label: resolveCrumbLabel(crumb, match, data, formatMessage),
        pathname: match.pathname,
        icon: IconComponent,
        permissionName,
      };
    });

  return (
    <Box>
      <Breadcrumbs
        sx={{
          display: { xs: 'none', md: 'flex' },
          '& .MuiBreadcrumbs-separator': {
            mx: 0.2,
            my: 0,
          },
        }}
        {...breadcrumbProps}>
        {breadcrumbs.map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const hasPermission = breadcrumb?.permissionName
            ? permissionConfig({
                userAccess,
                permissionName: breadcrumb?.permissionName,
              })
            : true;

          return (
            <Button
              {...(breadcrumb?.icon ? { startIcon: <breadcrumb.icon /> } : {})}
              sx={{
                py: 0,
                px: 0.5,
                minWidth: 'fit-content',
                cursor: isLast ? 'auto' : 'pointer',
              }}
              disabled={!hasPermission}
              disableRipple
              variant="text"
              size="medium"
              {...(isLast && { color: 'black' })}
              {...(!isLast && { href: breadcrumb.pathname })}
              key={breadcrumb.pathname}>
              {typeof breadcrumb.label === 'string'
                ? truncateString(breadcrumb.label, truncateLabelAfter)
                : breadcrumb.label}
            </Button>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}

export default BreadcrumbRouterBased;
